import { ObjectId } from 'mongodb';
import { WatchParty, PopulatedDatabaseWatchParty, Video } from '../types/types';
import api from './config';

const WATCH_API_URL = `${process.env.REACT_APP_SERVER_URL}/watch`;

/**
 * Fetches a watch party by its ID.
 *
 * @param watchPartyId - The ID of the watch party to fetch.
 * @returns The details of the watch party with the specified ID.
 * @throws Throws an error if the fetch fails or if the status code is not 200.
 */
export const getWatchPartyById = async (
  watchPartyId: string,
): Promise<PopulatedDatabaseWatchParty> => {
  const res = await api.get(`${WATCH_API_URL}/${watchPartyId}`);

  if (res.status !== 200) {
    throw new Error('Error when fetching watch party by ID');
  }

  return res.data;
};

/**
 * Fetches all watch parties.
 * @returns An array of all watch parties.
 */
export const getAllWatchParties = async (): Promise<PopulatedDatabaseWatchParty[]> => {
  const res = await api.get(`${WATCH_API_URL}/getAllWatchParties`);
  if (res.status !== 200) {
    throw new Error('Error when fetching all watch parties');
  }
  return res.data;
};
/**
 * Creates a new watch party.
 *
 * @param watchParty - The watch party data to be created.
 * @returns The newly created watch party data.
 * @throws Throws an error if the watch party creation fails.
 */
export const createWatchParty = async (
  watchParty: WatchParty,
): Promise<PopulatedDatabaseWatchParty> => {
  const res = await api.post(`${WATCH_API_URL}/createWatchParty`, watchParty);

  if (res.status !== 200) {
    throw new Error('Error when creating watch party');
  }

  return res.data;
};

/**
 * Adds a video to the watch party queue.
 *
 * @param watchPartyId - The ID of the watch party.
 * @param video - The video data to add.
 * @returns The updated watch party data.
 * @throws Throws an error if the operation fails.
 */
export const addVideoToQueue = async (
  watchPartyId: string,
  video: { title: string; url: string },
): Promise<PopulatedDatabaseWatchParty> => {
  const res = await api.post(`${WATCH_API_URL}/${watchPartyId}/addVideo`, video);

  if (res.status !== 200) {
    throw new Error('Error when adding video to queue');
  }

  return res.data;
};

/**
 * Updates the currently playing video for a watch party.
 *
 * @param watchPartyId - The ID of the watch party.
 * @param video - The video data to update.
 * @returns The updated watch party data.
 * @throws Throws an error if the operation fails.
 */
export const updateCurrentVideo = async (
  watchPartyId: string,
  video: { title: string; url: string },
): Promise<PopulatedDatabaseWatchParty> => {
  const res = await api.post(`${WATCH_API_URL}/${watchPartyId}/updateCurrentVideo`, video);

  if (res.status !== 200) {
    throw new Error('Error when updating current video');
  }

  return res.data;
};

export const getWatchPartiesByHost = async (
  host: string,
): Promise<PopulatedDatabaseWatchParty[]> => {
  const res = await api.get(`/${WATCH_API_URL}/getWatchPartiesByHost/${host}`);

  if (res.status !== 200) {
    throw new Error('Error when fetching watch parties by host');
  }

  return res.data;
};

export const getWatchPartyByName = async (name: string): Promise<PopulatedDatabaseWatchParty> => {
  const response = await api.get(`${WATCH_API_URL}/getWatchPartyByName/${name}`);

  if (response.status !== 200) {
    throw new Error('Error when fetching watch party by name');
  }

  return response.data;
};

export const addParticipantToWatchParty = async (
  watchPartyId: string,
  participant: string,
): Promise<PopulatedDatabaseWatchParty> => {
  const response = await api.post(`${WATCH_API_URL}/${watchPartyId}/addParticipant`, {
    participant,
  });

  if (response.status !== 200) {
    throw new Error('Error when adding participant to watch party');
  }

  return response.data;
};

export const removeParticipantFromWatchParty = async (
  watchPartyId: string,
  participantUsername: string,
): Promise<PopulatedDatabaseWatchParty> => {
  const response = await api.post(`${WATCH_API_URL}/${watchPartyId}/removeParticipant`, {
    participantUsername,
  });

  if (response.status !== 200) {
    throw new Error('Error when removing participant from watch party');
  }

  return response.data;
};

export const reorderVideoQueue = async (
  watchPartyId: string,
  video: Video,
  index: number,
): Promise<PopulatedDatabaseWatchParty> => {
  const response = await api.post(`${WATCH_API_URL}/${watchPartyId}/reorderQueue`, {
    video,
    index,
  });

  if (response.status !== 200) {
    throw new Error('Error when reordering video queue');
  }

  return response.data;
};

export const voteToMoveVideo = async (
  watchPartyId: string,
  videoId: ObjectId,
): Promise<PopulatedDatabaseWatchParty> => {
  const response = await api.post(`${WATCH_API_URL}/${watchPartyId}/voteToMoveVideo`, {
    videoId,
  });

  if (response.status !== 200) {
    throw new Error('Error when voting to move video');
  }

  return response.data;
};
